import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { PortalAdminService } from '../../../core/services/api-services/portal-admin.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BreadcrumbItem, BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { InputLimitDirective } from '../../../shared/components/directive/input-limit.directive';
declare var bootstrap: any;

// ───────────────────────────────────────────────────────────────
// ⬛ Access types
// ───────────────────────────────────────────────────────────────
interface AccessFlags {
  view: boolean;
  edit: boolean;
  add: boolean;
  review: boolean;
  level?: string | null;
}

interface NavChild {
  key: string;           // unique key used in the matrix/payload
  title: string;         // row label
  showLevel?: boolean;   // whether to show the Level selector
}

interface NavGroup {
  key: string;
  title: string;
  children?: NavChild[];
  showLevel?: boolean;   // used for modules with no children (leaf)
}

@Component({
  selector: 'app-users-and-requests',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PaginatorComponent, BreadcrumbComponent, InputLimitDirective],
  templateUrl: './users-and-requests.component.html',
  styleUrl: './users-and-requests.component.css'
})
export class UsersAndRequestsComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  dataSubscriber$!: Subscription;

  isTogglingStatus = false;
  selfDisableingUserId!: number;
  disableSelfDisable = false;

  // ───────────────────────────────────────────────────────────────
  // ⬛ Tab State
  // ───────────────────────────────────────────────────────────────
  activeTab: 'users' | 'requests' = 'users';

  switchTab(tab: 'users' | 'requests'): void {
    this.activeTab = tab;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Component State and Data
  // ───────────────────────────────────────────────────────────────
  @ViewChild('userFormOffcanvasEl') offcanvasElementRef!: ElementRef;

  portalUsers: any[] = [];
  filteredPortalUsers: any[] = [];

  pagedPortalUsers: any[] = [];

  PortalUsersPage = 1;
  PortalUsersPageSize = 10;
  PortalUsersPageSizeOptions = [5, 10, 25, 50];

  // ───────────────────────────────────────────────────────────────
  // ⬛ User Requests State
  // ───────────────────────────────────────────────────────────────
  requestUsers: any[] = [];
  filteredRequestUsers: any[] = [];
  pagedRequestUsers: any[] = [];
  requestPage = 1;
  requestPageSize = 10;

  isLoadingRequests = false;
  isApproving = false;
  isRejecting = false;
  selectedRequestUser: any = null;

  isLoading = false;
  error = '';
  searchTextUsers: string = '';
  searchTextRequests: string = '';
  userForm!: FormGroup;
  mode: 'add' | 'view' | 'edit' = 'add';
  selectedUser: any = null;

  // ───────────────────────────────────────────────────────────────
  // ⬛ Static Dropdown Lists
  // ───────────────────────────────────────────────────────────────
  departmentsList = [
    { id: 1, name: 'Admin', value: 'admin' },
    { id: 2, name: 'HR', value: 'hr' },
    { id: 3, name: 'Sales', value: 'sales' },
    { id: 4, name: 'Accounts', value: 'accounts' },
    { id: 5, name: 'IT', value: 'it' },
  ];

  designationsList = [
    { id: 1, name: 'Intern', value: 'intern' },
    { id: 2, name: 'Trainee', value: 'trainee' },
    { id: 3, name: 'Executive', value: 'executive' },
    { id: 4, name: 'Senior Executive', value: 'seniorExecutive' },
    { id: 5, name: 'Assistant Manager', value: 'assistantManager' },
    { id: 6, name: 'Manager', value: 'manager' },
    { id: 7, name: 'Senior Manager', value: 'seniorManager' },
    { id: 8, name: 'Lead', value: 'lead' },
    { id: 9, name: 'Associate Director', value: 'associateDirector' },
    { id: 10, name: 'Director', value: 'director' },
    { id: 11, name: 'Vice President', value: 'vicePresident' },
    { id: 12, name: 'Senior Vice President', value: 'seniorVicePresident' },
    { id: 13, name: 'Chief Operating Officer (COO)', value: 'coo' },
    { id: 14, name: 'Chief Technology Officer (CTO)', value: 'cto' },
    { id: 15, name: 'Chief Executive Officer (CEO)', value: 'ceo' }
  ];

  authorityLevelsList = [
    { id: 1, name: 'Level 1', value: 'level1' },
    { id: 2, name: 'Level 2', value: 'level2' },
    { id: 3, name: 'Level 3', value: 'level3' },
    { id: 4, name: 'Custom', value: 'custom' },
  ];

  // ───────────────────────────────────────────────────────────────
  // ⬛ Navigation model (parents without checkboxes; children with)
  // ───────────────────────────────────────────────────────────────
  navModules: NavGroup[] = [
    {
      key: 'clients',
      title: 'Clients',
      children: [
        { key: 'clients.employer', title: 'Employer', showLevel: true },
        { key: 'clients.mobileUsers', title: 'Mobile Users', showLevel: true },
        { key: 'clients.companyEmployees', title: 'Company Employees', showLevel: true },
      ],
    },
    {
      key: 'creditRisk',
      title: 'Credit & Risk',
      children: [
        { key: 'creditRisk.manageRollovers', title: 'Manage rollovers', showLevel: true },
        { key: 'creditRisk.manageCredit', title: 'Manage Credit', showLevel: true },
      ],
    },
    {
      key: 'opsRiskCompliance',
      title: 'Ops Risk & Compliance',
      children: [],
      showLevel: true,
    },
    {
      key: 'treasury',
      title: 'Treasury',
      children: [
        { key: 'treasury.cumulativeIncome', title: 'Cumulative Income', showLevel: true },
        { key: 'treasury.wageWithdrawals', title: 'Wage Withdrawals', showLevel: true },
        { key: 'treasury.walletWithdrawals', title: 'Wallet Status', showLevel: true },
        // { key: 'treasury.cixorPayments', title: 'CIXOR Payments', showLevel: true },
      ],
    },
    {
      key: 'manageCixorPayments',
      title: 'Manage CIXOR Payments',
      children: [
        { key: 'manageCixorPayments.cixorPayments', title: 'CIXOR Payments', showLevel: true },
        { key: 'manageCixorPayments.paymentHistory', title: 'Payment History', showLevel: true },
        { key: 'manageCixorPayments.payrollPayments', title: 'Payroll Payments', showLevel: true },
      ],
    },
    {
      key: 'fundManagement',
      title: 'Fund Management',
      children: [
        { key: 'fundManagement.contributorManagement', title: 'Contributor Management', showLevel: true },
        { key: 'fundManagement.summaryManagement', title: 'Summary Management', showLevel: true },
      ],
    },
    {
      key: 'configuration',
      title: 'Configuration',
      children: [
        { key: 'configuration.lateFeeConfiguration', title: 'Late Fee Configuration', showLevel: true },
        { key: 'configuration.industryConfiguration', title: 'Industry Configuration', showLevel: true },
      ],
    },
    {
      key: 'userManagement',
      title: 'User Management',
      children: [],
      showLevel: true,
    },
  ];

  // helpers
  hasChildren(g: NavGroup): boolean {
    return !!(g.children && g.children.length > 0);
  }
  childrenOf(g: NavGroup): NavChild[] {
    return g.children ?? [];
  }

  // Access matrix keyed by child key
  accessMatrix: Record<string, AccessFlags> = {};

  // ───────────────────────────────────────────────────────────────
  // ⬛ Access Level Logic (L1/L2/L3 + Custom)
  // ───────────────────────────────────────────────────────────────
  private readonly LEVEL_PRESETS: Record<string, Partial<AccessFlags>> = {
    level1: { view: true, edit: false, add: false, review: false },
    level2: { view: true, edit: true, add: true, review: false },
    level3: { view: true, edit: true, add: true, review: true },
    custom: {}, // marker for non-preset combinations
  };

  /** Normalize "L1|L2|L3|Custom" → "level1|level2|level3|custom" */
  private normalizeLevelFromDto(levelName?: string | null): 'level1' | 'level2' | 'level3' | 'custom' | null {
    if (!levelName) return null;
    const v = levelName.trim().toLowerCase();
    if (v === 'l1' || v === 'level1') return 'level1';
    if (v === 'l2' || v === 'level2') return 'level2';
    if (v === 'l3' || v === 'level3') return 'level3';
    if (v === 'custom') return 'custom';
    return null;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ NEW: central UI enable/disable helpers (do not break existing flows)
  // ───────────────────────────────────────────────────────────────
  /** True when offcanvas is in pure view mode (read-only) */
  get isReadOnly(): boolean {
    return this.mode === 'view';
  }

  /** True when checkboxes / level dropdowns should be editable */
  get canEditAccess(): boolean {
    // Allow in add + edit; block in view
    return this.mode !== 'view';
  }

  /** For binding in template or guarding handlers */
  isAccessDisabled(_key?: string): boolean {
    return !this.canEditAccess;
  }

  /** For the corporate role dropdown */
  isRoleDropdownDisabled(): boolean {
    return this.isReadOnly;
  }

  /**
   * Called when level dropdown changes for a given module row.
   * Updates that row’s access flags based on preset logic.
   */
  onAccessLevelChange(childKey: string): void {
    if (this.isAccessDisabled(childKey)) return; // guard in view mode
    const row = this.accessMatrix[childKey];
    if (!row || !row.level) return;

    const preset = this.LEVEL_PRESETS[row.level];
    if (preset) {
      this.accessMatrix[childKey] = {
        ...row,
        ...preset,
        level: row.level,
      };
    }
    this.markCorporateRoleAsCustom();
    this.accessMatrix = { ...this.accessMatrix };
  }

  /**
   * Called when any checkbox (view/edit/add/review) changes.
   * Detects matching preset; if none, sets level to 'custom'.
   */
  onAccessCheckboxChange(childKey: string): void {
    if (this.isAccessDisabled(childKey)) return; // guard in view mode
    const row = this.accessMatrix[childKey];
    if (!row) return;

    const matched = Object.entries(this.LEVEL_PRESETS).find(([lvl, preset]) =>
      lvl !== 'custom' &&
      preset.view === row.view &&
      preset.edit === row.edit &&
      preset.add === row.add &&
      preset.review === row.review
    );

    row.level = matched ? matched[0] : 'custom';
    this.markCorporateRoleAsCustom();
    this.accessMatrix = { ...this.accessMatrix };
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Constructor / Lifecycle
  // ───────────────────────────────────────────────────────────────
  constructor(
    private toaster: ToastrService,
    private portalAdminService: PortalAdminService,
    public authService: AuthService,
    private fb: FormBuilder,
    private breadcrumbService: BreadcrumbService
  ) {
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'User Management', icon: 'bi bi-people-fill' }
    ]);

    this.initAddUserForm();
    this.buildDefaultAccessMatrix(); // prepare default matrix for Add mode
    this.fetchPortalUsers();
    this.fetchRequestUsers();

    this.selfDisableingUserId = this.getCurrentUserId();

  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Get the Logged User
  // ───────────────────────────────────────────────────────────────
  private getCurrentUserId(): number {
    const user = this.authService.getUser();
    const userId = user?.portalAdminId;
    return (typeof userId === 'number' ? userId : Number(userId)) || 0;
  }

  checkIfSelfDisabling(targetUserId: number): void {
    this.disableSelfDisable = targetUserId === this.selfDisableingUserId;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Form Initialization and Validation
  // ───────────────────────────────────────────────────────────────
  initAddUserForm(): void {
    this.userForm = this.fb.group({
      firstName: [{ value: '', disabled: this.isDisabled('firstName'), }, [Validators.required, Validators.pattern(/^([A-Z][a-z]+)$/), Validators.maxLength(50)],],
      lastName: [{ value: '', disabled: this.isDisabled('lastName'), }, [Validators.required, Validators.pattern(/^([A-Z][a-z]+)$/), Validators.maxLength(50)],],
      userName: [{ value: '', disabled: this.isDisabled('userName') }],
      employeeId: [{ value: '', disabled: this.isDisabled('employeeId') }],
      password: [{ value: '', disabled: this.isDisabled('password') }],
      confirmPassword: [{ value: '', disabled: this.isDisabled('confirmPassword') }],
      email: this.fb.control(
        { value: '', disabled: this.isDisabled('email') },
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
          Validators.maxLength(254)
        ]
      ),
      mobileNumber: [{ value: '', disabled: this.isDisabled('mobileNumber'), }, [Validators.required, Validators.pattern(/^(?:0|\+94)(70|71|72|74|75|76|77|78)[0-9]{7}$/), Validators.maxLength(12)],],
      department: [{ value: '', disabled: this.isDisabled('department') }],
      designationId: [{ value: '', disabled: this.isDisabled('designationId') }],
    }, { validator: this.passwordsMatchValidator });
  }

  passwordsMatchValidator(formGroup: FormGroup): null | object {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Access Matrix helpers
  // ───────────────────────────────────────────────────────────────
  private buildDefaultAccessMatrix(): void {
    const matrix: Record<string, AccessFlags> = {};

    for (const group of this.navModules) {
      if (group.children && group.children.length > 0) {
        // parent with children
        for (const child of group.children) {
          const defaultLevel = child.showLevel ? this.authorityLevelsList[0].value : null;
          const preset = defaultLevel ? this.LEVEL_PRESETS[defaultLevel] : {};
          matrix[child.key] = {
            view: true,
            edit: false,
            add: false,
            review: false,
            level: defaultLevel,
            ...(preset || {}),
          };
        }
      } else {
        // leaf parent
        const defaultLevel = group.showLevel ? this.authorityLevelsList[0].value : null;
        const preset = defaultLevel ? this.LEVEL_PRESETS[defaultLevel] : {};
        matrix[group.key] = {
          view: true,
          edit: false,
          add: false,
          review: false,
          level: defaultLevel,
          ...(preset || {}),
        };
      }
    }

    this.accessMatrix = matrix;
  }

  private hydrateAccessMatrix(saved: Record<string, AccessFlags>) {
    for (const group of this.navModules) {
      if (group.children && group.children.length > 0) {
        for (const child of group.children) {
          if (saved?.[child.key]) {
            const src = saved[child.key];
            this.accessMatrix[child.key] = {
              view: !!src.view,
              edit: !!src.edit,
              add: !!src.add,
              review: !!src.review,
              level: child.showLevel ? (src.level ?? this.authorityLevelsList[0].value) : null,
            };
          }
        }
      } else {
        if (saved?.[group.key]) {
          const src = saved[group.key];
          this.accessMatrix[group.key] = {
            view: !!src.view,
            edit: !!src.edit,
            add: !!src.add,
            review: !!src.review,
            level: group.showLevel ? (src.level ?? this.authorityLevelsList[0].value) : null,
          };
        }
      }
    }
  }

  private buildAccessPayload() {
    return Object.entries(this.accessMatrix).map(([key, flags]) => ({
      key,
      view: !!flags.view,
      edit: !!flags.edit,
      add: !!flags.add,
      review: !!flags.review,
      levelName: flags.level ?? null
    }));
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ API Calls
  // ───────────────────────────────────────────────────────────────
  fetchPortalUsers(): void {
    this.isLoading = true;

    this.dataSubscriber$.add(
      this.portalAdminService.getApprovedPortalAdmins().subscribe({
        next: (response) => {
          if (response.success) {
            this.portalUsers = response.data;
            this.filteredPortalUsers = [...this.portalUsers];
          } else {
            this.error = 'Failed to load users.';
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.error = 'API error: ' + err.message;
          this.isLoading = false;
        }
      })
    );
  }

  fetchRequestUsers(): void {
    this.isLoadingRequests = true;

    this.dataSubscriber$.add(
      this.portalAdminService.getPendingPortalAdmins().subscribe({
        next: (response) => {
          if (response.success) {
            this.requestUsers = response.data;
            this.filteredRequestUsers = [...this.requestUsers];
            this.resetRequestPagination();
          }
          this.isLoadingRequests = false;
        },
        error: () => {
          this.isLoadingRequests = false;
        }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Form Mode Handlers (Add, View, Edit)
  // ───────────────────────────────────────────────────────────────
  openAddUserForm(): void {
    this.mode = 'add';
    this.selectedUser = null;
    this.userForm.enable();
    this.userForm.reset();
    this.selectedCorporateRole = 'custom'; // force custom on add
    this.buildDefaultAccessMatrix();        // reset matrix for new user
    this.resetPortalUserPagination();
  }

  openViewUserForm(user: any): void {
    this.mode = 'view';
    this.selectedUser = user;
    this.userForm.patchValue(user);
    this.userForm.disable();
    this.checkIfSelfDisabling(user.portalAdminId);

    // Pull saved access matrix if present
    if (user?.accessMatrix) {
      // supports:
      //  a) [{ key, view, edit, add, review, levelName }]
      //  b) [{ key, permissions:{...}, level }]
      //  c) map { [key]: { view, edit, add, review, level } }
      const mapped: Record<string, AccessFlags> = Array.isArray(user.accessMatrix)
        ? user.accessMatrix.reduce((acc: Record<string, AccessFlags>, row: any) => {
          if ('view' in row || 'edit' in row || 'add' in row || 'review' in row) {
            // (a) flat dto shape
            acc[row.key] = {
              view: !!row.view,
              edit: !!row.edit,
              add: !!row.add,
              review: !!row.review,
              level: this.normalizeLevelFromDto(row.levelName),
            };
          } else {
            // (b) permissions object
            acc[row.key] = {
              view: !!row.permissions?.view,
              edit: !!row.permissions?.edit,
              add: !!row.permissions?.add,
              review: !!row.permissions?.review,
              level: this.normalizeLevelFromDto(row.level),
            };
          }
          return acc;
        }, {})
        : user.accessMatrix; // (c) already a map

      this.buildDefaultAccessMatrix();   // ensure all keys exist
      this.hydrateAccessMatrix(mapped);  // overlay saved values

      // set role dropdown from API
      this.selectedCorporateRole = user.corporateRoleKey || 'custom';
    } else {
      this.buildDefaultAccessMatrix();
      this.selectedCorporateRole = 'custom';
    }
  }

  enterEditMode(): void {
    this.mode = 'edit';
    this.userForm.enable();

    // Disable non-editable fields in edit mode
    this.userForm.controls['userName'].disable();
    this.userForm.controls['employeeId'].disable();
    this.userForm.controls['email'].disable();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Field Visibility & Enabled State Logic
  // ───────────────────────────────────────────────────────────────
  isDisabled(field: string): boolean {
    if (this.mode === 'add') return false;
    if (this.mode === 'view') return true;

    if (this.mode === 'edit') {
      const editableFields = [
        'firstName',
        'lastName',
        'email',
        'mobileNumber',
        'department',
        'designationId'
      ];
      return !editableFields.includes(field);
    }

    return true;
  }

  isFieldVisible(field: string): boolean {
    if (this.mode === 'view' || this.mode === 'edit') {
      const visibleFields = [
        'firstName',
        'lastName',
        'userName',
        'employeeId',
        'email',
        'mobileNumber',
        'department',
        'designationId'
      ];
      return visibleFields.includes(field);
    }
    return true;
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Close Off Canvas
  // ───────────────────────────────────────────────────────────────
  closeOffcanvas(): void {
    const offcanvasEl = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl) {
      const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl) || new bootstrap.Offcanvas(offcanvasEl);
      bsOffcanvas.hide();
    }
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Form Actions (Submit/Add/Save)
  // ───────────────────────────────────────────────────────────────
  submitForm(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const formData = {
      portalAdminId: 0,
      employeeId: this.userForm.value.employeeId,
      designationId: this.userForm.value.designationId,
      firstName: this.userForm.value.firstName,
      lastName: this.userForm.value.lastName,
      userName: this.userForm.value.userName,
      mobileNumber: this.userForm.value.mobileNumber,
      email: this.userForm.value.email,
      passwordHash: '',
      passwordSalt: '',
      pinHash: '',
      pinSalt: '',
      isActive: true,
      createdDate: new Date().toISOString(),
      profilePic: null,
      invalidLogInAttempts: 0,
      isVerified: false,
      corporateRoleKey: this.selectedCorporateRole,
      accessMatrix: this.buildAccessPayload(),
    };

    this.dataSubscriber$.add(
      this.portalAdminService.createPortalAdmin(formData).subscribe({
        next: (response) => {
          if (response.success) {
            this.toaster.success(response.message || 'User request submitted. Awaiting Super Admin approval.', 'Success');
            this.fetchRequestUsers();
            this.closeOffcanvas();
          } else {
            this.toaster.error(response.message, 'Error');
          }
        },
        error: (error) => {
          const errorMessage = error.error?.message || error.message || 'Failed to add user.';
          this.toaster.error(errorMessage, 'Error');
        }
      })
    );
  }

  saveChanges(): void {
    if (this.userForm.invalid || !this.selectedUser) {
      this.userForm.markAllAsTouched();
      return;
    }

    const updatedData = {
      portalAdminId: this.selectedUser.portalAdminId,
      designationId: this.userForm.value.designationId,
      firstName: this.userForm.value.firstName,
      lastName: this.userForm.value.lastName,
      mobileNumber: this.userForm.value.mobileNumber,
      isActive: true,
      corporateRoleKey: this.selectedCorporateRole,
      accessMatrix: this.buildAccessPayload(),
    };

    this.dataSubscriber$.add(
      this.portalAdminService
        .updatePortalAdmin(this.selectedUser.portalAdminId, updatedData)
        .subscribe({
          next: () => {
            this.toaster.success('User updated successfully.', 'Updated');
            this.fetchPortalUsers();
            this.closeOffcanvas();
          },
          error: () => {
            this.toaster.error('Failed to update user.', 'Error');
          }
        })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Search Functionality
  // ───────────────────────────────────────────────────────────────
  performSearch(): void {
    this.performSearchUsers();
    this.performSearchRequests();
  }

  performSearchUsers(): void {
    const search = this.searchTextUsers.trim().toLowerCase();
    this.filteredPortalUsers = search
      ? this.portalUsers.filter(u => Object.values(u).some(v => String(v).toLowerCase().includes(search)))
      : [...this.portalUsers];
    this.resetPortalUserPagination();
  }

  performSearchRequests(): void {
    const search = this.searchTextRequests.trim().toLowerCase();
    this.filteredRequestUsers = search
      ? this.requestUsers.filter(u => Object.values(u).some(v => String(v).toLowerCase().includes(search)))
      : [...this.requestUsers];
    this.resetRequestPagination();
  }

  // ───────────────────────────────────────────────────────────────
  // ⬛ Corporate roles (Super Admin, Treasurer, etc.)
  // ───────────────────────────────────────────────────────────────

  corporateRolesList = [
    { value: 'superAdmin', name: 'Super Administrator' },
    { value: 'financeAdmin', name: 'Finance Admin' },
    { value: 'treasurer', name: 'Treasurer' },
    { value: 'userManager', name: 'User Manager' },
    { value: 'auditor', name: 'Auditor' },
    { value: 'custom', name: 'Custom' }
  ];

  selectedCorporateRole: string = 'custom';

  private readonly CORPORATE_ROLE_PRESETS: Record<string, Record<string, 'level1' | 'level2' | 'level3'>> = {
    superAdmin: {
      // Clients
      'clients': 'level3',
      'clients.employer': 'level3',
      'clients.mobileUsers': 'level3',
      'clients.companyEmployees': 'level3',

      // Credit & Risk
      'creditRisk': 'level3',
      'creditRisk.manageRollovers': 'level3',
      'creditRisk.manageCredit': 'level3',

      // Ops Risk & Compliance
      'opsRiskCompliance': 'level3',

      // Treasury
      'treasury': 'level3',
      'treasury.cumulativeIncome': 'level3',
      'treasury.wageWithdrawals': 'level3',
      'treasury.walletWithdrawals': 'level3',
      'treasury.creditLine': 'level3', // (exists in permission logic)

      // Manage CIXOR Payments
      'manageCixorPayments': 'level3',
      'manageCixorPayments.cixorPayments': 'level3',
      'manageCixorPayments.paymentHistory': 'level3',
      'manageCixorPayments.payrollPayments': 'level3',

      // Fund Management
      'fundManagement': 'level3',
      'fundManagement.summaryManagement': 'level3',
      'fundManagement.contributorManagement': 'level3',

      // Configuration
      'configuration': 'level3',
      'configuration.lateFeeConfiguration': 'level3',
      'configuration.industryConfiguration': 'level3',

      // User Management
      'userManagement': 'level3',
    },
    financeAdmin: {
      'creditRisk.manageRollovers': 'level3',
      'creditRisk.manageCredit': 'level3',
      'treasury.cumulativeIncome': 'level3',
      'treasury.wageWithdrawals': 'level3',
      'treasury.walletWithdrawals': 'level3',
      'treasury.creditLine': 'level3',
      'fundManagement.summaryManagement': 'level3',
      'fundManagement.contributorManagement': 'level3',
    },
    treasurer: {
      'treasury.cumulativeIncome': 'level3',
      'treasury.wageWithdrawals': 'level3',
      'treasury.walletWithdrawals': 'level3',
      'treasury.creditLine': 'level2',
      'fundManagement.summaryManagement': 'level3',
      'fundManagement.contributorManagement': 'level3',
    },
    clientManager: {
      'clients.employer': 'level3',
      'clients.mobileUsers': 'level3',
      'clients.companyEmployees': 'level3',
    },
    userManager: {
      'userManagement': 'level3',
    },
    auditor: {
      'clients.employer': 'level1',
      'clients.mobileUsers': 'level1',
      'creditRisk.manageRollovers': 'level1',
      'creditRisk.manageCredit': 'level3',
      'treasury.cumulativeIncome': 'level1',
      'treasury.wageWithdrawals': 'level1',
      'treasury.walletWithdrawals': 'level3',
      'treasury.creditLine': 'level1',
      'userManagement': 'level1',
      'opsRiskCompliance': 'level1',
      'fundManagement.summaryManagement': 'level3',
      'fundManagement.contributorManagement': 'level3',
    },
  };

  /** Helper: does a row key support level dropdown? */
  private keySupportsLevel(key: string): boolean {
    for (const g of this.navModules) {
      if (!g.children || g.children.length === 0) {
        if (g.key === key) return !!g.showLevel;
        continue;
      }
      const child = g.children.find(c => c.key === key);
      if (child) return !!child.showLevel;
    }
    return false;
  }

  /** When a corporate role is selected, apply its preset across the matrix. */
  onCorporateRoleChange(roleKey: string): void {
    if (this.isRoleDropdownDisabled()) return; // guard in view mode
    this.selectedCorporateRole = roleKey;
    if (roleKey === 'custom') return;

    const presetMap = this.CORPORATE_ROLE_PRESETS[roleKey];
    if (!presetMap) return;

    for (const key of Object.keys(this.accessMatrix)) {
      const targetLevel: 'level1' | 'level2' | 'level3' = presetMap[key] ?? 'level1';
      const lvlPreset = this.LEVEL_PRESETS[targetLevel];
      if (!lvlPreset) continue;

      this.accessMatrix[key] = {
        ...this.accessMatrix[key],
        view: !!lvlPreset.view,
        edit: !!lvlPreset.edit,
        add: !!lvlPreset.add,
        review: !!lvlPreset.review,
        level: this.keySupportsLevel(key) ? targetLevel : this.accessMatrix[key].level ?? null,
      };
    }

    this.accessMatrix = { ...this.accessMatrix };
  }

  /** Mark selected corporate role as custom (when user manually tweaks a row). */
  private markCorporateRoleAsCustom(): void {
    if (this.selectedCorporateRole !== 'custom') {
      this.selectedCorporateRole = 'custom';
    }
  }

  // Fast lookup for role name by value
  roleNameByValue: Record<string, string> = this.corporateRolesList
    .reduce((acc, r) => ({ ...acc, [r.value]: r.name }), {} as Record<string, string>);

  getRoleName(key?: string | null): string {
    if (!key) return 'Custom';
    return this.roleNameByValue[key] ?? 'Custom';
  }

  private updatePortalUserPage(): void {
    if (this.PortalUsersPage < 1) this.PortalUsersPage = 1;
    const start = (this.PortalUsersPage - 1) * this.PortalUsersPageSize;
    this.pagedPortalUsers = this.filteredPortalUsers.slice(start, start + this.PortalUsersPageSize);
  }

  private resetPortalUserPagination(): void {
    this.PortalUsersPage = 1;
    this.updatePortalUserPage();
  }

  onPortalUserPageChange(paginatedData: any[]): void {
    this.pagedPortalUsers = paginatedData;
  }

  clearForm(): void {
    this.userForm.reset();
    this.resetAccessMatrix();
    this.selectedCorporateRole = 'custom';
  }

  cancelEdit(): void {
    // Restore original values
    this.mode = 'view';
    // Reload original data or navigate back
    this.fetchPortalUsers();
  }

  resetAccessMatrix(): void {
    // Reset all access permissions to default state
    Object.keys(this.accessMatrix).forEach(key => {
      this.accessMatrix[key] = {
        view: true,
        edit: false,
        add: false,
        review: false,
        level: 'level1'
      };
    });
  }

  // ─────────────────────────────────────────────────────────────── //
  // ⬛ User Request – View
  // ─────────────────────────────────────────────────────────────── //
  openViewRequestForm(user: any): void {
    this.selectedRequestUser = user;
    this.selectedUser = user;
    this.mode = 'view';
    this.userForm.patchValue(user);
    this.userForm.disable();

    if (user?.accessMatrix) {
      const mapped: Record<string, AccessFlags> = Array.isArray(user.accessMatrix)
        ? user.accessMatrix.reduce((acc: Record<string, AccessFlags>, row: any) => {
            if ('view' in row || 'edit' in row || 'add' in row || 'review' in row) {
              acc[row.key] = {
                view: !!row.view,
                edit: !!row.edit,
                add: !!row.add,
                review: !!row.review,
                level: this.normalizeLevelFromDto(row.levelName),
              };
            } else {
              acc[row.key] = {
                view: !!row.permissions?.view,
                edit: !!row.permissions?.edit,
                add: !!row.permissions?.add,
                review: !!row.permissions?.review,
                level: this.normalizeLevelFromDto(row.level),
              };
            }
            return acc;
          }, {})
        : user.accessMatrix;

      this.buildDefaultAccessMatrix();
      this.hydrateAccessMatrix(mapped);
      this.selectedCorporateRole = user.corporateRoleKey || 'custom';
    } else {
      this.buildDefaultAccessMatrix();
      this.selectedCorporateRole = 'custom';
    }
  }

  isSuperAdmin(): boolean {
    return this.authService.getRoleKey() === 'superAdmin';
  }

  approveRequest(user: any): void {
    const reviewerId = this.getCurrentUserId();
    this.isApproving = true;
    this.dataSubscriber$.add(
      this.portalAdminService.reviewPortalAdminRequest(user.portalAdminId, 'Approved', reviewerId).subscribe({
        next: (res) => {
          if (res.success) {
            this.toaster.success('User approved. Credentials have been emailed.', 'Approved');
            this.fetchPortalUsers();
            this.fetchRequestUsers();
            this.closeOffcanvas();
          } else {
            this.toaster.error(res.message || 'Approval failed.', 'Error');
          }
          this.isApproving = false;
        },
        error: (err) => {
          this.toaster.error(err?.error?.message || 'Approval failed.', 'Error');
          this.isApproving = false;
        }
      })
    );
  }

  rejectRequest(user: any): void {
    const reviewerId = this.getCurrentUserId();
    this.isRejecting = true;
    this.dataSubscriber$.add(
      this.portalAdminService.reviewPortalAdminRequest(user.portalAdminId, 'Rejected', reviewerId).subscribe({
        next: (res) => {
          if (res.success) {
            this.toaster.success('User request rejected.', 'Rejected');
            this.fetchRequestUsers();
            this.closeOffcanvas();
          } else {
            this.toaster.error(res.message || 'Rejection failed.', 'Error');
          }
          this.isRejecting = false;
        },
        error: (err) => {
          this.toaster.error(err?.error?.message || 'Rejection failed.', 'Error');
          this.isRejecting = false;
        }
      })
    );
  }

  onRequestPageChange(paginatedData: any[]): void {
    this.pagedRequestUsers = paginatedData;
  }

  private resetRequestPagination(): void {
    this.requestPage = 1;
    const start = 0;
    this.pagedRequestUsers = this.filteredRequestUsers.slice(start, this.requestPageSize);
  }

  getApprovalStatusClass(status: string): string {
    switch (status) {
      case 'Approved': return 'badge-active';
      case 'Rejected': return 'badge-rejected';
      default:         return 'badge-pending';
    }
  }

  getApprovalStatusIcon(status: string): string {
    switch (status) {
      case 'Approved': return 'bi-check-circle-fill';
      case 'Rejected': return 'bi-x-circle-fill';
      default:         return 'bi-clock-fill';
    }
  }

  // ─────────────────────────────────────────────────────────────── //
  // ⬛ Enable/Disable User
  // ─────────────────────────────────────────────────────────────── //
  toggleUserStatus(): void {
    if (!this.selectedUser) return;

    const isCurrentlyActive = this.selectedUser.isActive;
    const userId = this.selectedUser.portalAdminId;
    const action$ = isCurrentlyActive
      ? this.portalAdminService.disablePortalAdmin(userId)
      : this.portalAdminService.enablePortalAdmin(userId);

    this.isTogglingStatus = true;

    this.dataSubscriber$.add(
      action$.subscribe({
        next: (response) => {
          if (response.success) {
            // Update local state immediately — no need to re-fetch entire list
            this.selectedUser.isActive = !isCurrentlyActive;

            // Sync the same user object in the portalUsers array
            const idx = this.portalUsers.findIndex(u => u.portalAdminId === userId);
            if (idx !== -1) this.portalUsers[idx].isActive = !isCurrentlyActive;

            const idx2 = this.filteredPortalUsers.findIndex(u => u.portalAdminId === userId);
            if (idx2 !== -1) this.filteredPortalUsers[idx2].isActive = !isCurrentlyActive;

            this.toaster.success(
              isCurrentlyActive
                ? response.message || 'User has been disabled successfully.'
                : response.message || 'User has been enabled successfully.',
              isCurrentlyActive ? 'User Disabled' : 'User Enabled'
            );
          } else {
            this.toaster.error(response.message || 'Failed to update user status.', 'Error');
          }
          this.isTogglingStatus = false;
        },
        error: (err) => {
          const msg = err?.error?.message || 'Failed to update user status.';
          this.toaster.error(msg, 'Error');
          this.isTogglingStatus = false;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }

}
